<script setup>
import { ref, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Auth from '../../../../api/auth';
import wootConstants from 'dashboard/constants/globals';
import agentsAPI from 'dashboard/api/agents';
import ardhisasaAgentsAPI from 'dashboard/api/ardhisasaAgents';
import { useArdhisasaTicketsStore } from 'dashboard/stores/ardhisasaTickets';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  provider: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
  ardhisasaUsername: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close']);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const store = useStore();
const { t } = useI18n();

const agentName = ref(props.name);
const agentAvailability = ref(props.availability);
const selectedRoleId = ref(props.customRoleId || props.type);
const agentCredentials = ref({ email: props.email });

const rules = {
  agentName: { required, minLength: minLength(1) },
  selectedRoleId: { required },
  agentAvailability: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
  agentAvailability,
});

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    disabled: props.availability === AVAILABILITY_STATUS_KEYS[index],
  }))
);

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      id: props.id,
      name: agentName.value,
      availability: agentAvailability.value,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
      payload.custom_role_id = null;
    }

    await store.dispatch('agents/update', payload);
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

const resetPassword = async () => {
  try {
    await Auth.resetPassword(agentCredentials.value);
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  }
};

const ardhisasaStore = useArdhisasaTicketsStore();
const isArdhisasaLinked = computed(() => !!props.ardhisasaUsername);
const flaggingPasswordSync = ref(false);
const reconcileUsername = ref('');
const reconciling = ref(false);

const flagPasswordSync = async () => {
  flaggingPasswordSync.value = true;
  try {
    await agentsAPI.passwordSync(props.id);
    useAlert(t('AGENT_MGMT.EDIT.ARDHISASA.FLAG_PASSWORD_SYNC_SUCCESS'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.ARDHISASA.FLAG_PASSWORD_SYNC_ERROR'));
  } finally {
    flaggingPasswordSync.value = false;
  }
};

const reconcileArdhisasa = async () => {
  if (!reconcileUsername.value) return;
  reconciling.value = true;
  try {
    await ardhisasaAgentsAPI.reconcile(
      { user_id: props.id, username: reconcileUsername.value },
      ardhisasaStore.token,
      ardhisasaStore.jwt
    );
    useAlert(t('AGENT_MGMT.EDIT.ARDHISASA.RECONCILE_SUCCESS'));
    emit('close');
  } catch (error) {
    const message = error?.response?.data?.error;
    useAlert(message || t('AGENT_MGMT.EDIT.ARDHISASA.RECONCILE_ERROR'));
  } finally {
    reconciling.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="w-full" @submit.prevent="editAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentAvailability.$error }">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          <select
            v-model="agentAvailability"
            @change="v$.agentAvailability.$touch"
          >
            <option
              v-for="status in availabilityStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
          <span v-if="v$.agentAvailability.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full pt-2 mt-2 border-t border-n-weak">
        <p class="text-sm text-n-slate-11">
          {{
            isArdhisasaLinked
              ? $t('AGENT_MGMT.EDIT.ARDHISASA.LINKED_LABEL', {
                  username: ardhisasaUsername,
                })
              : $t('AGENT_MGMT.EDIT.ARDHISASA.NOT_LINKED_LABEL')
          }}
        </p>

        <Button
          v-if="isArdhisasaLinked"
          faded
          slate
          type="button"
          size="small"
          class="mt-2"
          :label="$t('AGENT_MGMT.EDIT.ARDHISASA.FLAG_PASSWORD_SYNC_BUTTON')"
          :is-loading="flaggingPasswordSync"
          @click.prevent="flagPasswordSync"
        />

        <div
          v-else-if="ardhisasaStore.isAuthenticated"
          class="flex items-end gap-2 mt-2"
        >
          <label class="flex-1">
            {{ $t('AGENT_MGMT.EDIT.ARDHISASA.RECONCILE_LABEL') }}
            <input
              v-model="reconcileUsername"
              type="text"
              :placeholder="
                $t('AGENT_MGMT.EDIT.ARDHISASA.RECONCILE_PLACEHOLDER')
              "
            />
          </label>
          <Button
            faded
            slate
            type="button"
            size="small"
            :label="$t('AGENT_MGMT.EDIT.ARDHISASA.RECONCILE_BUTTON')"
            :disabled="!reconcileUsername"
            :is-loading="reconciling"
            @click.prevent="reconcileArdhisasa"
          />
        </div>
        <p
          v-if="!isArdhisasaLinked && ardhisasaStore.isAuthenticated"
          class="mt-1 text-xs text-n-slate-10"
        >
          {{ $t('AGENT_MGMT.EDIT.ARDHISASA.RECONCILE_WARNING') }}
        </p>
      </div>

      <div class="flex flex-row justify-start w-full gap-2 px-0 py-2">
        <div class="w-[50%] ltr:text-left rtl:text-right">
          <Button
            v-if="provider !== 'saml'"
            ghost
            type="button"
            icon="i-lucide-lock-keyhole"
            class="!px-2"
            :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
            @click.prevent="resetPassword"
          />
        </div>
        <div class="w-[50%] flex justify-end items-center gap-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isUpdating"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </div>
    </form>
  </div>
</template>
